import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext.js';
import { CheckCircle, AlertCircle } from 'lucide-react';

/**
 * Operator self-service profile page.
 * - Edit display name (persists via PATCH /operator/me).
 * - Change password (Firebase reauthenticateWithCredential + updatePassword).
 * - Roles shown as read-only chips.
 */
export function ProfilePage() {
  const { operator, user, updateDisplayName, reauthenticateAndUpdatePassword } = useAuth();
  const [displayName, setDisplayName] = useState(operator?.display_name ?? '');
  const [savingName, setSavingName] = useState(false);
  const [nameMessage, setNameMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const handleSaveName = async (e: React.FormEvent) => {
    e.preventDefault();
    setNameMessage(null);
    setSavingName(true);
    try {
      await updateDisplayName(displayName.trim() || null);
      setNameMessage({ kind: 'success', text: 'Display name updated.' });
    } catch (err: any) {
      setNameMessage({ kind: 'error', text: err?.message || 'Failed to update display name.' });
    } finally {
      setSavingName(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMessage(null);
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ kind: 'error', text: 'New passwords do not match.' });
      return;
    }
    if (newPassword.length < 8) {
      setPasswordMessage({ kind: 'error', text: 'Password must be at least 8 characters.' });
      return;
    }
    setChangingPassword(true);
    try {
      await reauthenticateAndUpdatePassword(currentPassword, newPassword);
      setPasswordMessage({ kind: 'success', text: 'Password updated.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      if (err?.code === 'auth/wrong-password' || err?.code === 'auth/invalid-credential') {
        setPasswordMessage({ kind: 'error', text: 'Current password is incorrect.' });
      } else if (err?.code === 'auth/weak-password') {
        setPasswordMessage({ kind: 'error', text: 'Password is too weak.' });
      } else if (err?.code === 'auth/requires-recent-login') {
        setPasswordMessage({
          kind: 'error',
          text: 'Please sign out and sign back in, then try again.',
        });
      } else {
        setPasswordMessage({ kind: 'error', text: err?.message || 'Failed to change password.' });
      }
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-grey-700">Profile</h1>
        <p className="text-grey-500 mt-1">Manage your operator profile.</p>
      </div>

      {/* Identity */}
      <section className="bg-surface rounded-lg border border-grey-200 p-6">
        <h2 className="text-lg font-semibold text-grey-700 mb-4">Identity</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-y-3 text-sm">
          <dt className="text-grey-500">Email</dt>
          <dd className="text-grey-700 font-medium">{user?.email ?? operator?.email ?? '—'}</dd>
          <dt className="text-grey-500">Status</dt>
          <dd className="text-grey-700 font-medium capitalize">{operator?.status ?? 'unknown'}</dd>
          <dt className="text-grey-500">Roles</dt>
          <dd className="flex flex-wrap gap-2">
            {(operator?.roles ?? []).length === 0 ? (
              <span className="text-grey-500 italic">No roles assigned</span>
            ) : (
              (operator?.roles ?? []).map((r) => (
                <span
                  key={r}
                  className="px-2 py-0.5 bg-primary-50 text-primary-700 text-xs font-medium rounded"
                >
                  {r}
                </span>
              ))
            )}
          </dd>
        </dl>
      </section>

      {/* Display name */}
      <section className="bg-surface rounded-lg border border-grey-200 p-6">
        <h2 className="text-lg font-semibold text-grey-700 mb-4">Display Name</h2>
        <form onSubmit={handleSaveName} className="space-y-4">
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Optional"
            maxLength={120}
            className="w-full px-4 py-3 border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-04 focus:border-transparent"
          />
          {nameMessage && <FormMessage {...nameMessage} />}
          <button
            type="submit"
            disabled={savingName}
            className="px-5 py-2 bg-primary-04 text-white font-semibold rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
          >
            {savingName ? 'Saving…' : 'Save'}
          </button>
        </form>
      </section>

      {/* Password */}
      <section className="bg-surface rounded-lg border border-grey-200 p-6">
        <h2 className="text-lg font-semibold text-grey-700 mb-4">Change Password</h2>
        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label htmlFor="current-pw" className="block text-sm font-medium text-grey-700 mb-1">
              Current password
            </label>
            <input
              id="current-pw"
              type="password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full px-4 py-3 border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-04 focus:border-transparent"
            />
          </div>
          <div>
            <label htmlFor="new-pw" className="block text-sm font-medium text-grey-700 mb-1">
              New password
            </label>
            <input
              id="new-pw"
              type="password"
              required
              minLength={8}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-3 border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-04 focus:border-transparent"
              placeholder="At least 8 characters"
            />
          </div>
          <div>
            <label htmlFor="confirm-pw" className="block text-sm font-medium text-grey-700 mb-1">
              Confirm new password
            </label>
            <input
              id="confirm-pw"
              type="password"
              required
              minLength={8}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-04 focus:border-transparent"
            />
          </div>
          {passwordMessage && <FormMessage {...passwordMessage} />}
          <button
            type="submit"
            disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
            className="px-5 py-2 bg-primary-04 text-white font-semibold rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
          >
            {changingPassword ? 'Updating…' : 'Update Password'}
          </button>
        </form>
      </section>
    </div>
  );
}

function FormMessage({ kind, text }: { kind: 'success' | 'error'; text: string }) {
  if (kind === 'success') {
    return (
      <div className="flex items-center gap-2 text-success-600 text-sm">
        <CheckCircle className="h-4 w-4" />
        {text}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 text-danger-600 text-sm">
      <AlertCircle className="h-4 w-4" />
      {text}
    </div>
  );
}
